/**
 * Pure helpers for `<ModelGrid>` — no React, no DOM, fully unit-testable.
 *
 * Three responsibilities:
 *   1. Address translation (`colToA1`) — 1-based column index → A/B/.../AA
 *      string, mirroring Excel/A1 notation. Pure mirror of the server's
 *      `a1FromRowCol` at `apps/api/src/modules/conversations/conversations.service.v2.ts:103`
 *      so the grid's lookup keys match what `getModelView` produced.
 *   2. Cell lookup (`findCellAt`) — `(layout, row, col) → GridCell | null`.
 *      `SheetLayout.cells` is sparse (only non-empty cells) and unsorted;
 *      a Map<key, cell> built once per layout makes the per-tick lookup O(1).
 *   3. Value resolution (`resolveValue`) — input cells read from
 *      `inputValues[namedRange]`, formula/output cells read from
 *      `cellValues[${sheet}!${A1}]`. Mirrors the server-side resolver at
 *      `conversations.service.v2.ts:625-651` so values match end-to-end.
 */
import type { GridCell, SheetLayout } from '@arcanum-hq/shared-types';

/**
 * 1-based column index → A1 column letters. `1 → 'A'`, `26 → 'Z'`,
 * `27 → 'AA'`, `703 → 'AAA'`. Uses the bijective base-26 algorithm
 * (no zero digit).
 */
export function colToA1(col: number): string {
  if (col < 1) return '';
  let n = col;
  let s = '';
  while (n > 0) {
    n--;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

/**
 * `${sheet}!${A1}${row}` — the canonical cell key used by `cellValues` from
 * `GET /model-view`. Centralized here so the server-side projection
 * (a1FromRowCol) and the client-side lookup share the exact same string
 * format; any drift in formatting would silently miss cells.
 */
export function cellKey(sheetName: string, row: number, col: number): string {
  return `${sheetName}!${colToA1(col)}${row}`;
}

/**
 * Parse an A1-notation cell address (e.g. `"AA12"`) into 1-based `(row, col)`.
 * Returns `null` on malformed input — callers should treat that as "skip"
 * rather than throwing (the canonical model schemas have been validated
 * upstream, so a parse miss here means a stale/corrupt schema row).
 *
 * Inverse of `colToA1`. Used by `getTableInputRegions` in
 * `components/builder/model-view/cell-style.ts` to derive `(rowMin, colMin,
 * rowMax, colMax)` bounds from a `tableInput.topLeftCell` /
 * `tableInput.bottomRightCell` pair.
 */
export function parseA1(address: string): { row: number; col: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(address);
  if (!m) return null;
  const letters = m[1];
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col = col * 26 + (letters.charCodeAt(i) - 64); // 'A' = 65 → 1
  }
  const row = Number.parseInt(m[2], 10);
  if (!Number.isFinite(row) || row < 1 || col < 1) return null;
  return { row, col };
}

/**
 * Build a `${row}-${col}` → GridCell index from a sparse `SheetLayout.cells`
 * array. Memoize this per-layout in the rendering component (via `useMemo`
 * keyed on `layout.name`/`cells`) so the per-tick `findCellAt` is O(1).
 */
export function buildCellIndex(layout: SheetLayout): Map<string, GridCell> {
  const index = new Map<string, GridCell>();
  for (const cell of layout.cells) {
    index.set(`${cell.row}-${cell.col}`, cell);
  }
  return index;
}

/**
 * Lookup helper for direct callers (mostly tests). Production code should
 * call `buildCellIndex(layout)` once and `index.get(...)` per cell to amortize
 * the build cost across the grid render pass.
 */
export function findCellAt(
  layout: SheetLayout,
  row: number,
  col: number
): GridCell | null {
  const index = buildCellIndex(layout);
  return index.get(`${row}-${col}`) ?? null;
}

/**
 * Resolve the display value for a cell. Pure mirror of the server's
 * `getModelView` resolution loop:
 *   - input cells (with namedRange) → agent_state.inputValues[namedRange]
 *   - formula/output cells → cellValues[${sheet}!${A1}]
 *   - label cells → cell.text (raw text from the layout artifact;
 *     populated by the agents extractor since model-view-fidelity Phase 4,
 *     2026-04-27. Pre-Phase-4 layouts have `text === undefined` → falls
 *     through to null, matching prior empty-state behavior.)
 *   - empty / unrecognized → null
 *
 * Returns `null` instead of `undefined` so cell renderers can rely on a
 * single empty-state branch (matches the API contract — server ALSO returns
 * `null` on `getValueAt` failures or on missing inputValues entries).
 */
export function resolveValue(
  cell: GridCell,
  cellValues: Record<string, unknown>,
  inputValues: Record<string, unknown>,
  sheetName: string
): unknown {
  if (cell.cellType === 'input' && cell.namedRange) {
    return inputValues[cell.namedRange] ?? null;
  }
  if (cell.cellType === 'formula' || cell.cellType === 'output') {
    const computed = cellValues[cellKey(sheetName, cell.row, cell.col)];
    // builder-ux-overhaul FEATURE_END (2026-05-22): WS4 label fallback.
    // String-result formula cells (e.g. UnitMix!B35-B39 expense labels mirrored
    // as `+DealSummary!Jxx`) carry their workbook label in `cell.text` (populated
    // by the WS4 extractGridCell populate-gate widening). When the engine has NO
    // computed value for the cell (null/undefined/empty — e.g. the formula
    // resolves to nothing in an un-populated model, as `UnitMix!B38` "Management
    // Fee (%)" does), fall back to `cell.text` so the label still renders. Band
    // siblings whose formula DOES compute a string ("Payroll"/"Insurance"/…) are
    // unaffected — their non-null computed value wins. WS4's populate-gate fix was
    // inert without this because `resolveValue` previously only read `text` for
    // `cellType === 'label'`.
    if (
      (computed === null || computed === undefined || computed === '') &&
      cell.text != null
    ) {
      return cell.text;
    }
    return computed ?? null;
  }
  if (cell.cellType === 'label') {
    return cell.text ?? null;
  }
  return null;
}
